import { router } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AppHeader } from '@/components/app-header'
import {
  Users,
  Calendar,
  AlertCircle,
  Activity,
  UserPlus,
  CalendarPlus,
  Eye,
  Clock,
  MapPin,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts'

interface DashboardStats {
  active_patients: number
  appointments_today: number
  urgent_cases: number
  ready_for_treatment: number
}

interface PipelineStage {
  stage: string
  count: number
}

interface Task {
  id: number
  type: 'appointment' | 'follow_up'
  patient_id: number
  patient_name: string
  due_date: string
  title: string
  status: string
  location?: string
  duration_minutes?: number
  completed_at?: string
  updated_at: string
}

interface Tasks {
  todo: Task[]
  in_progress: Task[]
  completed: Task[]
}

interface Activity {
  id: number
  type: string
  description: string
  patient_id: number
  patient_name: string
  occurred_at: string
}

interface ClinicianDashboardProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  stats: DashboardStats
  pipeline_distribution: PipelineStage[]
  tasks: Tasks
  recent_activities: Activity[]
}

export default function ClinicianDashboard({
  auth,
  stats,
  pipeline_distribution,
  tasks,
  recent_activities,
}: ClinicianDashboardProps) {
  const chartConfig = {
    count: {
      label: 'Patients',
      color: 'hsl(217, 91%, 60%)', // Vibrant blue matching the page aesthetic
    },
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    }
  }

  const getTaskDueColor = (task: Task) => {
    if (task.status === 'completed') return 'text-green-600 dark:text-green-400'
    if (task.status === 'overdue') return 'text-red-600 dark:text-red-400'

    const dueDate = new Date(task.due_date)
    const today = new Date()
    const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays < 0) return 'text-red-600 dark:text-red-400'
    if (diffDays === 0) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-green-600 dark:text-green-400'
  }

  const getPatientInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'patient_registered':
        return <UserPlus className="h-4 w-4" />
      case 'case_status_changed':
        return <Activity className="h-4 w-4" />
      case 'treatment_started':
        return <CheckCircle2 className="h-4 w-4" />
      case 'follow_up_completed':
        return <CheckCircle2 className="h-4 w-4" />
      default:
        return <Activity className="h-4 w-4" />
    }
  }

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Clinician Dashboard' }]} />

      <div className="flex flex-1 flex-col bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="@container/main flex flex-1 flex-col gap-4 py-4 md:gap-6 md:py-6">
          {/* Quick Actions */}
          <div className="px-4 lg:px-6">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common workflows and navigation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Button
                    variant="outline"
                    className="justify-start gap-2 h-auto py-3"
                    onClick={() => router.visit('/register')}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-950 dark:to-indigo-950">
                      <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Add Patient</div>
                      <div className="text-xs text-muted-foreground">Register new patient</div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-2 h-auto py-3"
                    onClick={() => router.visit('/admin/appointments')}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-950 dark:to-purple-950">
                      <CalendarPlus className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Schedule</div>
                      <div className="text-xs text-muted-foreground">Book appointment</div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-2 h-auto py-3"
                    onClick={() => router.visit('/admin/patients')}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-950 dark:to-pink-950">
                      <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Patients</div>
                      <div className="text-xs text-muted-foreground">View all patients</div>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="justify-start gap-2 h-auto py-3"
                    onClick={() => router.visit('/admin/appointments')}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-pink-100 to-red-100 dark:from-pink-950 dark:to-red-950">
                      <Eye className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Appointments</div>
                      <div className="text-xs text-muted-foreground">View calendar</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 px-4 md:grid-cols-2 lg:grid-cols-4 lg:px-6">
            <Card className="from-blue-50/50 to-card bg-gradient-to-b dark:from-blue-950/30 dark:to-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Patients</CardTitle>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
                  <Users className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.active_patients}</div>
                <p className="text-xs text-muted-foreground">With active care cases</p>
              </CardContent>
            </Card>

            <Card className="from-indigo-50/50 to-card bg-gradient-to-b dark:from-indigo-950/30 dark:to-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Appointments Today</CardTitle>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.appointments_today}</div>
                <p className="text-xs text-muted-foreground">Scheduled for today</p>
              </CardContent>
            </Card>

            <Card className="from-red-50/50 to-card bg-gradient-to-b dark:from-red-950/30 dark:to-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Urgent Cases</CardTitle>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-red-600 to-orange-600">
                  <AlertCircle className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.urgent_cases}</div>
                <p className="text-xs text-muted-foreground">Requiring immediate attention</p>
              </CardContent>
            </Card>

            <Card className="from-purple-50/50 to-card bg-gradient-to-b dark:from-purple-950/30 dark:to-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ready for Treatment</CardTitle>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-pink-600">
                  <Activity className="h-5 w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.ready_for_treatment}</div>
                <p className="text-xs text-muted-foreground">Ready to start treatment</p>
              </CardContent>
            </Card>
          </div>

          {/* Pipeline Chart and Recent Activities */}
          <div className="grid gap-4 px-4 lg:grid-cols-3 lg:px-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Care Pipeline Overview</CardTitle>
                <CardDescription>Patient distribution across pipeline stages</CardDescription>
              </CardHeader>
              <CardContent>
                {pipeline_distribution.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pipeline_distribution}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                          dataKey="stage"
                          tick={{ fill: 'hsl(var(--muted-foreground))' }}
                          angle={-45}
                          textAnchor="end"
                          height={100}
                        />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="count" fill="var(--color-count)" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                ) : (
                  <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                    No pipeline data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest system events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recent_activities.length > 0 ? (
                    recent_activities.slice(0, 8).map((activity) => (
                      <div key={activity.id} className="flex items-start gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-950 dark:to-indigo-950">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm leading-tight">{activity.description}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <button
                              onClick={() => router.visit(`/admin/patients/${activity.patient_id}`)}
                              className="font-medium hover:text-primary"
                            >
                              {activity.patient_name}
                            </button>
                            <span>•</span>
                            <span>{getRelativeTime(activity.occurred_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
                      No recent activity
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Task Kanban Board */}
          <div className="grid gap-4 px-4 lg:grid-cols-3 lg:px-6">
            {/* To Do Column */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-950 dark:to-orange-950">
                    <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  To Do
                  <Badge variant="secondary" className="ml-auto">
                    {tasks.todo.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tasks.todo.length > 0 ? (
                    tasks.todo.slice(0, 10).map((task) => (
                      <div
                        key={`${task.type}-${task.id}`}
                        className="group rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors cursor-pointer"
                        onClick={() => router.visit(`/admin/patients/${task.patient_id}`)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-xs font-semibold text-white">
                              {getPatientInitials(task.patient_name)}
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className="text-sm font-medium leading-tight">{task.patient_name}</p>
                              <p className="text-xs text-muted-foreground">{task.title}</p>
                              <div className="flex items-center gap-2 text-xs">
                                <Badge
                                  variant={task.type === 'appointment' ? 'default' : 'outline'}
                                  className="text-xs"
                                >
                                  {task.type === 'appointment' ? 'Appointment' : 'Follow-up'}
                                </Badge>
                                <span className={getTaskDueColor(task)}>{formatDate(task.due_date)}</span>
                              </div>
                              {task.location && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <MapPin className="h-3 w-3" />
                                  {task.location}
                                </div>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                      No tasks to do
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* In Progress Column */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-950 dark:to-indigo-950">
                    <Activity className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  In Progress
                  <Badge variant="secondary" className="ml-auto">
                    {tasks.in_progress.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tasks.in_progress.length > 0 ? (
                    tasks.in_progress.slice(0, 10).map((task) => (
                      <div
                        key={`${task.type}-${task.id}`}
                        className="group rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors cursor-pointer"
                        onClick={() => router.visit(`/admin/patients/${task.patient_id}`)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-xs font-semibold text-white">
                              {getPatientInitials(task.patient_name)}
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className="text-sm font-medium leading-tight">{task.patient_name}</p>
                              <p className="text-xs text-muted-foreground">{task.title}</p>
                              <div className="flex items-center gap-2 text-xs">
                                <Badge
                                  variant={task.type === 'appointment' ? 'default' : 'outline'}
                                  className="text-xs"
                                >
                                  {task.type === 'appointment' ? 'Appointment' : 'Follow-up'}
                                </Badge>
                                <span className={getTaskDueColor(task)}>{formatDate(task.due_date)}</span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                      No tasks in progress
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Completed Column */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-950 dark:to-emerald-950">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  Completed
                  <Badge variant="secondary" className="ml-auto">
                    {tasks.completed.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {tasks.completed.length > 0 ? (
                    tasks.completed.slice(0, 10).map((task) => (
                      <div
                        key={`${task.type}-${task.id}`}
                        className="group rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors cursor-pointer opacity-70"
                        onClick={() => router.visit(`/admin/patients/${task.patient_id}`)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-green-600 to-emerald-600 text-xs font-semibold text-white">
                              {getPatientInitials(task.patient_name)}
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className="text-sm font-medium leading-tight">{task.patient_name}</p>
                              <p className="text-xs text-muted-foreground">{task.title}</p>
                              <div className="flex items-center gap-2 text-xs">
                                <Badge variant="outline" className="text-xs">
                                  Completed
                                </Badge>
                                <span className="text-muted-foreground">
                                  {formatDate(task.completed_at || task.updated_at)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                      No completed tasks
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
